import { DropdownProvider } from '@psl/components/dropdown/context';
import { Portal } from '@psl/components/modal/Portal';
import { useBoundingRect } from '@psl/functions/ux/useBoundingRect';
import { useFocusEffect } from '@react-navigation/core';
import { NavigationAction, useNavigation } from '@react-navigation/native';
import { StackNavigationOptions } from '@react-navigation/stack';
import { AnimatePresence, MotiView } from 'moti';
import React, {
  createContext,
  forwardRef,
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { Easing } from 'react-native-reanimated';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import { isMobileLayout } from '../components/styles/responsive';
import { useBehaviorSubjectUpdater } from '../functions/hooks/useBehaviorSubject';
import { useCreation } from '../functions/hooks/useCreation';
import { useResponsiveContext } from '../functions/responsive/ResponsiveContext';
import { useLocalObservable } from '../functions/rxReact/useLocalObservable';
import { FCC } from '../utils/react';

export interface ModalWrapperRef {
  dismiss(): void;
}

export interface ModalWrapperProps {
  children?: ReactNode;
  dismissible?: boolean;
  portal?: boolean;
  width?: number;
  height?: number;
  backgroundColor?: string;
  backdropBackgroundColor?: string;
  shadowColor?: string;
  decoratedView?: ReactElement;
  contentContainerStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
  standalone?: boolean;
  withDropdownBounds?: boolean;
  onDismiss?: () => void;
  onClose?: () => void;
}

type ModalWrapperContextProps = {
  size$: BehaviorSubject<{ width: number; height: number }>;
};
export const ModalWrapperContext =
  React.createContext<ModalWrapperContextProps>(null as any);

export const useModalWrapperSize = (size: {
  width?: number;
  height?: number;
}) => {
  const { size$ } = useContext(ModalWrapperContext);
  const oldSize = useRef<typeof size$.value>();
  useFocusEffect(
    useCallback(() => {
      oldSize.current = size$.value;
      const newSize = {
        width: size.width ?? oldSize.current!.width,
        height: size.height ?? oldSize.current!.height,
      };
      setTimeout(() => {
        size$.next(newSize);
      }, 0);
      return () => {
        if (oldSize.current) {
          size$.next(oldSize.current!);
          oldSize.current = undefined;
        }
      };
    }, [size$, size.height, size.width]),
  );
};

export const ModalPresentationContext = createContext<{
  modalPresented$: BehaviorSubject<number[]>;
}>(null as any);

export const ModalPresentationContextProvider: FCC = memo(({ children }) => {
  const modalPresented$ = useCreation(
    () => new BehaviorSubject<number[]>([]),
    [],
  );
  return (
    <ModalPresentationContext.Provider value={{ modalPresented$ }}>
      {children}
    </ModalPresentationContext.Provider>
  );
});

export const useModalPresentationContext = () =>
  useContext(ModalPresentationContext);

export const ModalWrapper = forwardRef<ModalWrapperRef, ModalWrapperProps>(
  (props, ref) => {
    const [open, setOpen] = useState(true); // for portal
    const [show, setShow] = useState(true);
    const navigation = useNavigation();
    const { rpx } = useResponsiveContext();
    const { top } = useSafeAreaInsets();
    const continueAction = useRef<NavigationAction>();
    const { modalPresented$ } = useModalPresentationContext();
    const updateModalPresented = useBehaviorSubjectUpdater(modalPresented$);
    const index = useRef(navigation.getState().index).current;
    const size$ = useCreation(
      () =>
        new BehaviorSubject({
          width: props.width ?? rpx(400),
          height: props.height ?? rpx(700),
        }),
      [props.height, props.width, rpx],
    );

    const size = useLocalObservable(
      () =>
        size$.pipe(
          distinctUntilChanged(
            (x, y) => x.width === y.width && x.height === y.height,
          ),
        ),
      [size$],
      size$.value,
    );

    useImperativeHandle(
      ref,
      () => ({
        dismiss: () => {
          setShow(false);
        },
      }),
      [],
    );

    useEffect(() => {
      if (!show || props.standalone) {
        return;
      }
      return navigation.addListener('beforeRemove', e => {
        e.preventDefault();
        continueAction.current = e.data.action;
        setShow(false);
      });
    }, [navigation, props.standalone, show]);

    const context = useMemo(() => ({ size$ }), [size$]);

    useEffect(() => {
      updateModalPresented(ids => [...new Set([...ids, index])]);
      return () => {
        updateModalPresented(ids => {
          const set = new Set(ids);
          set.delete(index);
          return [...set];
        });
      };
    }, [index, updateModalPresented]);

    const [contentRef, contentBoundingRect, { update: updateBoundingRect }] =
      useBoundingRect(props.withDropdownBounds ?? false);

    return (
      <Portal open={open} enabled={props.portal}>
        <ModalWrapperContext.Provider value={context}>
          <DropdownProvider boundingRect={contentBoundingRect}>
            <AnimatePresence
              onExitComplete={() => {
                setOpen(false);
                if (!props.standalone) {
                  if (continueAction.current != null) {
                    navigation.dispatch(continueAction.current);
                  } else {
                    navigation.goBack();
                  }
                }
                props.onClose?.();
              }}
            >
              {show && (
                <MotiView
                  nativeID="modal-backdrop"
                  style={props.style}
                  from={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'timing', duration: 300 }}
                  exitTransition={{
                    type: 'spring',
                    overshootClamping: true,
                  }}
                  css={`
                    flex: 1;
                    overflow: hidden;
                    padding-top: ${top}px;
                    background-color: ${props.backdropBackgroundColor ??
                    'rgba(0, 0, 0, 0.54)'};
                  `}
                >
                  <KeyboardAvoidingView
                    behavior={'padding'}
                    css={`
                      flex: 1;
                      justify-content: center;
                    `}
                  >
                    <TouchableWithoutFeedback
                      onPress={() => {
                        if (props.dismissible) {
                          if (props.onDismiss) {
                            props.onDismiss();
                          }
                          if (!props.standalone) {
                            navigation.goBack();
                          }
                        }
                      }}
                    >
                      <View style={StyleSheet.absoluteFill} />
                    </TouchableWithoutFeedback>
                    <MotiView
                      ref={contentRef}
                      css={`
                        margin: ${rpx(30)}px ${rpx(5)}px;
                        background-color: ${props.backgroundColor ?? 'white'};
                        border-radius: ${rpx(15)}px;
                        overflow: hidden;
                        align-self: center;
                        max-width: ${size.width}px;
                        height: ${size.height}px;
                        width: 100%;
                        ${props.shadowColor
                          ? `box-shadow: 0 0 44px ${props.shadowColor}`
                          : ''};
                      `}
                      from={{ translateY: rpx(400) }}
                      animate={{ translateY: 0 }}
                      transition={{
                        type: 'timing',
                        duration: 250,
                        easing: Easing.in(Easing.linear),
                      }}
                      exit={{ translateY: rpx(400) }}
                      exitTransition={{
                        type: 'spring',
                        overshootClamping: true,
                      }}
                      style={[
                        Platform.OS === 'web'
                          ? {
                              maxHeight: '95vh',
                            }
                          : undefined,
                        props.contentContainerStyle,
                      ]}
                      onDidAnimate={updateBoundingRect}
                    >
                      <SafeAreaInsetsContext.Provider
                        value={{ top: 0, left: 0, right: 0, bottom: 0 }}
                      >
                        {props.children}
                      </SafeAreaInsetsContext.Provider>
                    </MotiView>
                    {props.decoratedView ? (
                      <View
                        style={StyleSheet.absoluteFill}
                        pointerEvents="box-none"
                        nativeID="modal-wrapper-decorated-view-wrapper"
                        css={`
                          justify-content: center;
                        `}
                      >
                        <MotiView
                          pointerEvents="box-none"
                          css={`
                            margin: ${rpx(30)}px ${rpx(5)}px;
                            background-color: transparent;
                            align-self: center;
                            max-width: ${size.width}px;
                            height: ${size.height}px;
                            width: 100%;
                          `}
                          from={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            type: 'timing',
                            duration: 250,
                            easing: Easing.in(Easing.linear),
                          }}
                          exit={{ opacity: 0 }}
                          exitTransition={{
                            type: 'spring',
                            overshootClamping: true,
                          }}
                          style={
                            Platform.OS === 'web'
                              ? {
                                  maxHeight: '95vh',
                                }
                              : undefined
                          }
                        >
                          {props.decoratedView}
                        </MotiView>
                      </View>
                    ) : null}
                  </KeyboardAvoidingView>
                </MotiView>
              )}
            </AnimatePresence>
          </DropdownProvider>
        </ModalWrapperContext.Provider>
      </Portal>
    );
  },
);

export const centerModalOption: StackNavigationOptions = {
  presentation: 'transparentModal',
  cardOverlayEnabled: true,
  cardStyle: { backgroundColor: 'transparent' },
  animationEnabled: false,
  headerShown: false,
};

export const adaptiveWrapperOptions = (
  options?:
    | StackNavigationOptions
    | ((isMobile: boolean) => StackNavigationOptions | undefined),
): StackNavigationOptions => {
  const isMobile = isMobileLayout();
  if (typeof options === 'function') {
    options = options(isMobile);
  }
  if (isMobile) {
    return {
      headerShown: false,
      presentation: 'modal',
      headerStatusBarHeight: 0,
      ...options,
    };
  }
  return { ...centerModalOption, ...options };
};

export const adaptiveCenterModalOptions = (
  options?:
    | StackNavigationOptions
    | ((isMobile: boolean) => StackNavigationOptions | undefined),
): StackNavigationOptions => {
  const isMobile = isMobileLayout();
  if (typeof options === 'function') {
    options = options(isMobile);
  }
  if (isMobile) {
    return {
      headerShown: false,
      headerStatusBarHeight: 0,
      ...options,
    };
  }
  return { ...centerModalOption, ...options };
};
